/**
 * ByodChart — Renders chart widgets using the design system's UniversalChart.
 *
 * Self-manages chart kind (no API refetch on kind change — pure display swap).
 * Data transformations live in ../../utils/chart-data.
 *
 * Parent controls sizing via `classNames.container`. Parent can receive
 * `onChartStyleChange` to persist the chosen kind (e.g. into widget_config_json).
 */

import * as React from 'react';

import {UniversalChart} from '@spaced-out/ui-design-system/lib/components/Charts';
import type {
  ChartKind,
  UniversalChartProps,
} from '@spaced-out/ui-design-system/lib/components/Charts/UniversalChart';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {classify} from 'src/utils/classify';

import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {useAgencyData} from 'src/hooks/useAgencyData';
import type {ChartConfig, ChartStyle} from 'src/types/byod';
import {
  buildCategoricalSeries,
  buildProportionSeries,
  isCategoricalKind,
  isProportionKind,
} from 'src/components/analytics-genesis/byod/utils/chart-data';

import css from './byod-chart.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ByodChartClassNames = {
  /** Outer div around UniversalChart — parent controls sizing here. */
  container?: string;
  /** UniversalChart card wrapper. */
  wrapper?: string;
  /** Card content area (between header and highchart). */
  content?: string;
  /** Highcharts container. */
  highChart?: string;
};

export type ByodChartProps = {
  label: string;
  chartConfig: ChartConfig | null;
  data: Record<string, unknown>[];
  /** Initial chart style. Falls back to chart_config.default_chart_style. */
  chartStyle?: ChartStyle;
  /**
   * Called when the user switches chart kind via the in-chart dropdown.
   * Parent can use this to persist the new default (canvas tile → store).
   * Not required — kind is self-managed inside ByodChart.
   */
  onChartStyleChange?: (kind: ChartStyle) => void;
  /** Rendered in UniversalChart's header actions row (right of title). */
  headerActions?: React.ReactNode;
  /** Explicit Highcharts chart height in px. When set, disables responsive
   *  mode so the chart fits a fixed-height container (e.g. canvas tile). */
  chartHeight?: number;
  isLoading?: boolean;
  classNames?: ByodChartClassNames;
  testId?: string;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ByodChart({
  label,
  chartConfig,
  data,
  chartStyle,
  onChartStyleChange,
  headerActions,
  chartHeight,
  isLoading = false,
  classNames,
  testId,
}: ByodChartProps) {
  const {t, language} = useLabelTranslation();
  const {timezone: agencyTimezone} = useAgencyData();

  // Chart kind is owned internally. Seeded from prop > config default > 'column'.
  // Kind switching is display-only (no API refetch).
  const initialKind: ChartStyle =
    chartStyle ?? chartConfig?.default_chart_style ?? 'column';
  const [localKind, setLocalKind] = React.useState<ChartStyle>(initialKind);

  // If the seed changes (e.g. parent restored persisted config), re-sync.
  React.useEffect(() => {
    setLocalKind(initialKind);
  }, [initialKind]);

  const isProportion = isProportionKind(localKind);
  const isFunnel = localKind === 'funnel';
  const isDonutKind = localKind === 'donut';

  let resolvedKind: ChartKind;
  if (isProportion) {
    resolvedKind = localKind;
  } else if (isCategoricalKind(localKind)) {
    resolvedKind = localKind;
  } else {
    resolvedKind = 'column';
  }

  const handleKindChange = React.useCallback(
    (k: ChartKind) => {
      const kind = k as ChartStyle;
      setLocalKind(kind);
      onChartStyleChange?.(kind);
    },
    [onChartStyleChange],
  );

  const series = React.useMemo(() => {
    if (!chartConfig || data.length === 0) {
      return [];
    }
    if (isProportionKind(localKind)) {
      return buildProportionSeries(data, chartConfig, {
        agencyTimezone,
        language,
      });
    }
    return buildCategoricalSeries(data, chartConfig, {
      agencyTimezone,
      language,
    });
  }, [chartConfig, data, localKind, agencyTimezone, language]);

  const xAxisLabel = chartConfig?.x_axis?.label;
  const hasEmptyData = !isLoading && series.length === 0;

  // When more than 5 data points, make the plot area scrollable.
  // 80px per point ensures comfortable spacing; below 5 points,
  // no scrollable area is needed — chart fits naturally.
  // Proportion charts (funnel, donut) have no x-axis to scroll.
  const dataPointCount = series.length > 0 ? series[0].data.length : 0;
  const PX_PER_POINT = 80;
  const needsScroll = !isProportion && dataPointCount > 5;
  const scrollableMinWidth = needsScroll ? dataPointCount * PX_PER_POINT : 0;

  const chartOpts = React.useMemo(
    () => ({
      ...(chartHeight ? {height: chartHeight} : {}),
      reflow: true,
      ...(needsScroll
        ? {scrollablePlotArea: {minWidth: scrollableMinWidth}}
        : {}),
    }),
    [chartHeight, needsScroll, scrollableMinWidth],
  );

  const xAxisOpts = React.useMemo(
    () => ({
      ...(xAxisLabel ? {title: {text: xAxisLabel}} : {}),
      ...(needsScroll ? {scrollbar: {enabled: true}} : {}),
    }),
    [xAxisLabel, needsScroll],
  );

  const yAxisOpts = React.useMemo(() => ({title: {text: ''}}), []);
  const legendOpts = React.useMemo(() => ({enabled: true, shadow: false}), []);

  const chartClassNames = React.useMemo(
    () => ({
      wrapper: classNames?.wrapper,
      content: classNames?.content,
      highChart: classify(
        classNames?.highChart,
        isFunnel && css.funnelHighChart,
        isDonutKind && css.donutHighChart,
      ),
    }),
    [
      classNames?.wrapper,
      classNames?.content,
      classNames?.highChart,
      isFunnel,
      isDonutKind,
    ],
  );

  const emptyText = (
    <EmptyState
      title={t('No_result_found', 'No result found')}
      description={t(
        'No_data_available_for_the_selected_filters',
        'No data available for the selected filters.',
      )}
      imageVariant="data"
    />
  );

  // UniversalChartProps is a discriminated union keyed on chartKind.
  // We resolve chartKind at runtime so we assert the combined props.
  const chartProps = {
    chartKind: resolvedKind,
    series,
    cardTitle: label,
    isLoading,
    hasEmptyData,
    emptyText,
    responsive: !chartHeight,
    chart: chartOpts,
    legend: legendOpts,
    showChartKindSwitcher: false as const,
    onChartKindChange: handleKindChange,
    headerActions,
    classNames: chartClassNames,
    testId: 'byod-chart',
    // xAxis/yAxis only apply to categorical kinds; proportion charts ignore them.
    ...(!isProportion ? {xAxis: xAxisOpts, yAxis: yAxisOpts} : {}),
  } as UniversalChartProps;

  return (
    <div
      className={classify(css.byodChartContainer, classNames?.container)}
      data-testid={testId}
    >
      <UniversalChart {...chartProps} />
    </div>
  );
}
